import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import {
  ActivationCodeService,
  RoleService,
  FormService,
} from '../../../services';
@Component({
  selector: 'app-activation-code-manager',
  standalone: false,
  templateUrl: './activation-code-manager.html',
  styleUrl: './activation-code-manager.css',
})
export class ActivationCodeManager implements OnInit {

  loading = false;
  errorMsg = '';

  allCodes: any[] = [];
  filteredCodes: any[] = [];
  pagedCodes: any[] = [];
  searchQuery = '';
  page = 0;
  size = 10;

  allRoles: any[] = [];
  allForms: any[] = [];

  selectedCodes: Set<number> = new Set();
  bulkRevokeLoading = false;
  bulkRevokeError = '';
  showBulkRevokeModal = false;

  showGenerateModal = false;
  generateForm: any = {
    roleId: '',
    formId: '',
    count: 1,
    expiresAt: '',
  };
  generateLoading = false;
  generateError = '';

  showRevokeModal = false;
  selectedCode: any = null;
  revokeLoading = false;
  revokeError = '';

  constructor(
    private activationCodeService: ActivationCodeService,
    private roleService: RoleService,
    private formService: FormService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.fetchAll();
  }

  fetchAll() {
    this.loading = true;
    this.errorMsg = '';
    this.cdr.detectChanges();

    Promise.all([
      this.activationCodeService.getActivationCodes().toPromise(),
      this.roleService.getRoles().toPromise(),
      this.formService.getForms().toPromise(),
    ]).then(([codesRes, rolesRes, formsRes]: any[]) => {
      this.allCodes = codesRes?.data ?? codesRes?.content ?? [];

      const roles = rolesRes?.data ?? rolesRes?.content ?? [];
      this.allRoles = roles.filter((r: any) => r.name?.toLowerCase() !== 'super_admin');
      this.allForms = formsRes?.data ?? formsRes?.content ?? [];
      this.applyFilter();
      this.loading = false;
      this.cdr.detectChanges();
    }).catch((err: any) => {
      console.log(err)
      this.errorMsg = err?.error?.message || 'Failed to load data.';
      this.loading = false;
      this.cdr.detectChanges();
    });
  }

  fetchCodesSilently() {
    this.activationCodeService.getActivationCodes().subscribe({
      next: (res: any) => {
        this.allCodes = res?.data ?? res?.content ?? [];
        this.applyFilter();
        this.cdr.detectChanges();
      },
      error: () => {}
    });
  }

  applyFilter() {
    const q = this.searchQuery.toLowerCase().trim();
    this.filteredCodes = q
      ? this.allCodes.filter(c =>
          c.code?.toLowerCase().includes(q) ||
          c.roleName?.toLowerCase().includes(q) ||
          c.formName?.toLowerCase().includes(q) ||
          c.status?.toLowerCase().includes(q)
        )
      : [...this.allCodes];
    this.page = 0;
    this.selectedCodes.clear();
    this.updatePage();
  }

  updatePage() {
    const start = this.page * this.size;
    this.pagedCodes = this.filteredCodes.slice(start, start + this.size);
  }

  get totalPages(): number { return Math.ceil(this.filteredCodes.length / this.size); }
  get hasNext(): boolean { return this.page < this.totalPages - 1; }
  get hasPrev(): boolean { return this.page > 0; }

  nextPage() { if (!this.hasNext) return; this.page++; this.updatePage(); }
  prevPage() { if (!this.hasPrev) return; this.page--; this.updatePage(); }
  onSearch() { this.applyFilter(); }
  getMinValue(a: number, b: number) { return Math.min(a, b); }

  getRoleById(id: number) { return this.allRoles.find(r => r.id === id); }
  getFormById(id: number) { return this.allForms.find(f => f.id === id); }

  isExpired(expiresAt: string): boolean {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  }

  getStatusLabel(code: any): string {
    if (code.revoked || code.status === 'REVOKED') return 'Revoked';
    if (code.used || code.status === 'USED') return 'Used';
    if (this.isExpired(code.expiresAt)) return 'Expired';
    return 'Active';
  }

  getStatusClass(code: any): string {
    const s = this.getStatusLabel(code);
    if (s === 'Active') return 'status-active';
    if (s === 'Used') return 'status-used';
    if (s === 'Expired') return 'status-expired';
    return 'status-revoked';
  }

  canRevoke(code: any): boolean {
    return this.getStatusLabel(code) === 'Active';
  }

  toggleSelect(id: number) {
    if (this.selectedCodes.has(id)) {
      this.selectedCodes.delete(id);
    } else {
      this.selectedCodes.add(id);
    }
    this.cdr.detectChanges();
  }

  isSelected(id: number): boolean {
    return this.selectedCodes.has(id);
  }

  get allPageSelected(): boolean {
    const revokable = this.pagedCodes.filter(c => this.canRevoke(c));
    return revokable.length > 0 && revokable.every(c => this.selectedCodes.has(c.id));
  }

  toggleSelectAll() {
    const revokable = this.pagedCodes.filter(c => this.canRevoke(c));
    if (this.allPageSelected) {
      revokable.forEach(c => this.selectedCodes.delete(c.id));
    } else {
      revokable.forEach(c => this.selectedCodes.add(c.id));
    }
    this.cdr.detectChanges();
  }

  get selectedCount(): number { return this.selectedCodes.size; }

  clearSelection() {
    this.selectedCodes.clear();
    this.cdr.detectChanges();
  }

  openBulkRevokeModal() {
    this.bulkRevokeError = '';
    this.showBulkRevokeModal = true;
  }

  closeBulkRevokeModal() {
    if (this.bulkRevokeLoading) return;
    this.showBulkRevokeModal = false;
    this.bulkRevokeError = '';
  }

  confirmBulkRevoke() {
    this.bulkRevokeLoading = true;
    this.bulkRevokeError = '';
    this.cdr.detectChanges();

    const ids = Array.from(this.selectedCodes);
    const requests = ids.map(id => this.activationCodeService.revokeActivationCode(id).toPromise());

    Promise.all(requests).then(() => {
      this.bulkRevokeLoading = false;
      this.showBulkRevokeModal = false;
      this.selectedCodes.clear();
      this.fetchCodesSilently();
      this.cdr.detectChanges();
    }).catch((err: any) => {
      this.bulkRevokeLoading = false;
      this.bulkRevokeError = err?.error?.message || 'Failed to revoke some codes.';
      this.cdr.detectChanges();
    });
  }

  openGenerateModal() {
    this.generateForm = { roleId: '', formId: '', count: 1, expiresAt: '' };
    this.generateError = '';
    this.showGenerateModal = true;
  }

  closeGenerateModal() {
    if (this.generateLoading) return;
    this.showGenerateModal = false;
    this.generateError = '';
  }

  submitGenerate() {
    if (!this.generateForm.roleId) { this.generateError = 'Please select a role.'; return; }
    if (!this.generateForm.formId) { this.generateError = 'Please select a form.'; return; }
    if (!this.generateForm.count || this.generateForm.count < 1) { this.generateError = 'Count must be at least 1.'; return; }
    if (!this.generateForm.expiresAt) { this.generateError = 'Please set an expiry date.'; return; }

    this.generateLoading = true;
    this.generateError = '';
    this.cdr.detectChanges();

    this.activationCodeService.generateBatch({
      roleId: Number(this.generateForm.roleId),
      formId: Number(this.generateForm.formId),
      count: Number(this.generateForm.count),
      expiresAt: new Date(this.generateForm.expiresAt).toISOString(),
    }).subscribe({
      next: () => {
        this.generateLoading = false;
        this.showGenerateModal = false;
        this.fetchCodesSilently();
        this.cdr.detectChanges();
      },
      error: (err: any) => {
        this.generateLoading = false;
        this.generateError = err?.error?.message || 'Failed to generate codes.';
        this.cdr.detectChanges();
      }
    });
  }

  openRevokeModal(code: any) {
    this.selectedCode = code;
    this.revokeError = '';
    this.showRevokeModal = true;
  }

  closeRevokeModal() {
    if (this.revokeLoading) return;
    this.showRevokeModal = false;
    this.revokeError = '';
    this.selectedCode = null;
  }

  confirmRevoke() {
    if (!this.selectedCode) return;
    this.revokeLoading = true;
    this.revokeError = '';
    this.cdr.detectChanges();

    this.activationCodeService.revokeActivationCode(this.selectedCode.id).subscribe({
      next: () => {
        this.revokeLoading = false;
        this.showRevokeModal = false;
        this.selectedCode = null;
        this.fetchCodesSilently();
        this.cdr.detectChanges();
      },
      error: (err: any) => {
        this.revokeLoading = false;
        this.revokeError = err?.error?.message || 'Failed to revoke code.';
        this.cdr.detectChanges();
      }
    });
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  get minExpiryDate(): string {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
  }
}